/* =============================================================
   VALORATIME — HOME (réplica limpia) — JS
   Swiper para hero / carrusel de productos / oferta, y un
   countdown "rotativo" en JS puro para reemplazar el countdown
   de LayoutHub (que apuntaba a una fecha fija ya vencida:
   2026-04-08 — el original tenía el mismo problema apenas
   pasaba esa fecha, así que acá se genera una ventana nueva por
   sesión en vez de a una fecha fija).
   ============================================================= */
(function () {
  // Bug real encontrado al revisar mobile: cada sección del home (hero,
  // productos, oferta, aviso) incluye su propio <script src="valoratime-home.js">,
  // así que en una página con varias este archivo se ejecuta más de una
  // vez. Sin este guard, "new Swiper(...)" se llama repetidas veces sobre
  // el mismo elemento. Con el guard, el cuerpo del script corre una sola
  // vez por carga de página sin importar cuántas veces se incluya el
  // <script>.
  if (window.__vtHomeInit) return;
  window.__vtHomeInit = true;

  if (typeof Swiper === 'undefined') return;

  // Bug real #2, más sutil: inicializar Swiper apenas corre este script
  // (defer, es decir en DOMContentLoaded) hacía que el carrusel de
  // "Ofertas" midiera su contenedor ANTES de que el layout terminara de
  // asentarse (grid + fuentes), y Swiper calculaba un ancho absurdo —
  // exactamente 2^25 px — que desbordaba toda la página en mobile.
  // Reproducido y confirmado: re-creando el mismo swiper con las mismas
  // opciones después de la carga completa siempre da el ancho correcto,
  // así que el problema es puramente de timing, no de configuración.
  // Solución: esperar al evento "load" (todo cargado y layout estable)
  // antes de construir los swipers, y forzar un update() extra en el
  // siguiente frame como red de seguridad.
  // Bug real #3, encontrado en la auditoría de "a prueba de cambios
  // futuros": estas tres búsquedas usaban querySelector (singular),
  // así que sólo tomaban la PRIMERA sección de cada tipo. Si en algún
  // momento se duplica una sección "Hero"/"Productos"/"Oferta" desde
  // el editor de temas (algo que Shopify permite con un clic y que no
  // debería romper nada), la copia se quedaba sin su carrusel
  // inicializado. Con querySelectorAll + forEach, cualquier cantidad
  // de instancias de cada sección se inicializa por igual — y cada
  // elemento se marca con un data-attribute para no inicializarse dos
  // veces si initSwipers() corre más de una vez (ver listener de
  // "shopify:section:load" más abajo, para cuando el editor inyecta
  // una sección nueva sin recargar la página).
  // Bug real de accesibilidad (auditoría): los slides inactivos del
  // carrusel (hero y oferta) seguían siendo alcanzables por teclado —
  // Tab podía dejar el foco en el botón de un slide que ni se ve (fuera
  // de pantalla, desplazado por el transform del carrusel). El módulo
  // a11y de Swiper no cubre esto solo (no retira el foco de elementos
  // interactivos dentro de slides ocultos), así que se maneja a mano:
  // cada slide inactivo se marca aria-hidden y sus enlaces/botones
  // pasan a tabindex="-1" (fuera del recorrido de Tab); el slide activo
  // vuelve a ser accesible normalmente. Se re-ejecuta en cada cambio de
  // slide (init, slideChange, autoplay) para que nunca quede desfasado.
  function updateSlideFocusability(swiper) {
    swiper.slides.forEach((slideEl) => {
      const isActive = slideEl.classList.contains('swiper-slide-active');
      const focusable = slideEl.querySelectorAll('a, button, input, [tabindex]');
      if (isActive) {
        slideEl.removeAttribute('aria-hidden');
        focusable.forEach((el) => el.removeAttribute('tabindex'));
      } else {
        slideEl.setAttribute('aria-hidden', 'true');
        focusable.forEach((el) => el.setAttribute('tabindex', '-1'));
      }
    });
  }

  function initSwipers(root) {
    const scope = root || document;

    scope.querySelectorAll('.vt-hero:not([data-vt-swiper-init])').forEach((heroEl) => {
      heroEl.setAttribute('data-vt-swiper-init', '');
      const autoplayOn = heroEl.dataset.autoplay !== 'false';
      const autoplaySpeed = (parseFloat(heroEl.dataset.autoplaySpeed) || 5) * 1000;
      new Swiper(heroEl, {
        loop: true,
        autoplay: autoplayOn ? { delay: autoplaySpeed, disableOnInteraction: false } : false,
        pagination: heroEl.querySelector('.swiper-pagination') ? { el: '.swiper-pagination', clickable: true } : false,
        navigation: heroEl.querySelector('.swiper-button-next')
          ? { nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' }
          : false,
        on: {
          init: (swiper) => updateSlideFocusability(swiper),
          slideChange: (swiper) => updateSlideFocusability(swiper),
          slideChangeTransitionEnd: (swiper) => updateSlideFocusability(swiper),
        },
      });
      // El único "wow moment" del sitio: título/subtítulo/botón entran
      // en secuencia corta una sola vez al cargar (ver valoratime-home.css).
      // Doble requestAnimationFrame para asegurar que el navegador ya
      // pintó el estado inicial (opacity:0) antes de agregar la clase
      // — si no, a veces el navegador "salta" directo al estado final
      // sin animar la transición.
      requestAnimationFrame(() => requestAnimationFrame(() => heroEl.classList.add('vt-hero-ready')));
    });

    scope.querySelectorAll('.vt-products-carousel:not([data-vt-swiper-init])').forEach((productsEl) => {
      productsEl.setAttribute('data-vt-swiper-init', '');
      new Swiper(productsEl, {
        slidesPerView: 'auto',
        spaceBetween: 20,
        freeMode: true,
        navigation: false,
      });
    });

    scope.querySelectorAll('.vt-sale-content:not([data-vt-swiper-init])').forEach((saleEl) => {
      saleEl.setAttribute('data-vt-swiper-init', '');
      const saleAutoplayOn = saleEl.dataset.autoplay !== 'false';
      const saleAutoplaySpeed = (parseFloat(saleEl.dataset.autoplaySpeed) || 4.5) * 1000;
      const saleSwiper = new Swiper(saleEl, {
        autoplay: saleAutoplayOn ? { delay: saleAutoplaySpeed, disableOnInteraction: false } : false,
        on: {
          init: (swiper) => updateSlideFocusability(swiper),
          slideChange: (swiper) => updateSlideFocusability(swiper),
          slideChangeTransitionEnd: (swiper) => updateSlideFocusability(swiper),
        },
      });
      requestAnimationFrame(() => saleSwiper.update());
    });
  }

  if (document.readyState === 'complete') {
    initSwipers();
  } else {
    window.addEventListener('load', () => initSwipers());
  }

  // Mismo caso que en valoratime-motion.js: si el editor de temas
  // agrega/duplica una de estas secciones en vivo, se inicializa sola.
  document.addEventListener('shopify:section:load', (event) => initSwipers(event.target));

  /* Tarjetas de producto: elegir variante (círculos) + agregar al
     carrito de verdad. Usa delegación de eventos sobre todo el
     documento así funciona sin importar cuántas tarjetas haya o
     cómo las reordene Swiper. */
  document.addEventListener('click', function (event) {
    const swatch = event.target.closest('.vt-swatch');
    if (!swatch) return;
    const card = swatch.closest('.vt-product-card');
    if (!card) return;

    card.querySelectorAll('.vt-swatch').forEach((el) => el.classList.remove('is-active'));
    swatch.classList.add('is-active');

    const mainImg = card.querySelector('[data-vt-main-image]');
    if (mainImg && swatch.dataset.image) mainImg.src = swatch.dataset.image;

    const variantInput = card.querySelector('[data-vt-variant-input]');
    if (variantInput) variantInput.value = swatch.dataset.variantId;

    const priceEl = card.querySelector('[data-vt-current-price]');
    if (priceEl && swatch.dataset.price) priceEl.textContent = swatch.dataset.price;

    const compareEl = card.querySelector('[data-vt-compare]');
    const hasDiscount = swatch.dataset.hasDiscount === 'true';
    if (compareEl) {
      if (hasDiscount) {
        compareEl.textContent = swatch.dataset.compare;
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    const ribbon = card.querySelector('[data-vt-ribbon]');
    if (ribbon) {
      if (hasDiscount && swatch.dataset.savings) {
        // El texto ya viene traducido desde Liquid con un marcador
        // (__AMT__) en vez del monto — acá sólo se reemplaza el
        // marcador, nunca se escribe "Save" a mano.
        const template = ribbon.dataset.vtSaveTemplate || 'Save __AMT__';
        ribbon.textContent = template.replace('__AMT__', swatch.dataset.savings);
        ribbon.style.display = '';
      } else {
        ribbon.style.display = 'none';
      }
    }

    const atcForm = card.querySelector('[data-vt-atc-form]');
    const atcBtn = card.querySelector('[data-vt-atc-btn]');
    const atcLabel = card.querySelector('[data-vt-atc-label]');
    const available = swatch.dataset.available === 'true';
    if (atcBtn) atcBtn.disabled = !available;
    if (atcLabel && atcForm) {
      atcLabel.textContent = available ? atcForm.dataset.vtI18nAddToCart : atcForm.dataset.vtI18nSoldOut;
    }
  });

  document.addEventListener('submit', function (event) {
    const form = event.target.closest('[data-vt-atc-form]');
    if (!form) return;
    event.preventDefault();

    const btn = form.querySelector('[data-vt-atc-btn]');
    const label = form.querySelector('[data-vt-atc-label]');
    const variantId = form.querySelector('[data-vt-variant-input]')?.value;
    if (!variantId || !btn) return;

    const originalLabel = label ? label.textContent : '';
    // Los 3 textos de estado (adding/added/error) ya vienen traducidos
    // desde Liquid en el propio form — nunca se escriben a mano acá,
    // así el idioma real del visitante se respeta en todo el flujo.
    const i18nAdding = form.dataset.vtI18nAdding || 'Adding…';
    const i18nAdded = form.dataset.vtI18nAdded || 'Added';
    const i18nError = form.dataset.vtI18nError || 'Error adding to cart';
    btn.disabled = true;
    if (label) label.textContent = i18nAdding;

    fetch(window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          // Shopify devuelve { status, message, description } en errores (ej: sin stock)
          throw new Error(data.description || data.message || 'Cart error');
        }
        if (label) label.textContent = i18nAdded;
        btn.classList.add('is-added');
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: data, sourceId: 'valoratime-product-card', data: {} },
          })
        );
        setTimeout(() => {
          btn.classList.remove('is-added');
          btn.disabled = false;
          if (label) label.textContent = originalLabel;
        }, 2000);
      })
      .catch(() => {
        if (label) label.textContent = i18nError;
        btn.disabled = false;
        setTimeout(() => {
          if (label) label.textContent = originalLabel;
        }, 2500);
      });
  });

  /* Countdown "urgencia" — cuenta regresiva a una ventana fija de horas
     desde que se abrió la página en esta sesión (sessionStorage), en vez
     de una fecha calendario que se vence y queda rota. */
  const countdownEl = document.querySelector('[data-vt-countdown]');
  if (countdownEl) {
    const hours = parseFloat(countdownEl.dataset.vtCountdown) || 6;
    const key = 'vtCountdownEnd';
    let endTime = sessionStorage.getItem(key);
    if (!endTime) {
      endTime = Date.now() + hours * 60 * 60 * 1000;
      sessionStorage.setItem(key, endTime);
    }
    endTime = parseInt(endTime, 10);

    function tick() {
      const diff = Math.max(0, endTime - Date.now());
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      countdownEl.innerHTML =
        `<span>${String(h).padStart(2, '0')}h</span><span>${String(m).padStart(2, '0')}m</span><span>${String(s).padStart(2, '0')}s</span>`;
      if (diff > 0) requestAnimationFrame(() => setTimeout(tick, 1000));
    }
    tick();
  }
})();
